<template>
  <div
    class="form-group ama-custom-field"
    :class="{
      focused: isFocus,
      'floated-label': topLabel,
      validated: validated,
      invalid: (errors.length > 0),
    }"
  >
    <div class="form-group--content">
      <div
        class="custom-label"
        :class="{ 'custom-label--textarea': type == 'textarea' }"
      >
        {{ label }}
      </div>
      <slot name="info"></slot>
      <div v-if="type == 'ama-select'" class="custom-select data-hj-allow">
        <input
          type="text"
          @focus="openSelect"
          @keypress.prevent
          @blur="customBlur"
          readonly
          :value="customLabel"
          class="form-select"
        />
        <div class="custom-select--options d-none">
          <slot name="options" :change="customChange"></slot>
        </div>
      </div>
      <select
        v-else-if="type == 'select'"
        :value="innerValue"
        class="form-select data-hj-allow"
        data-lpignore="true"
        @change="handleInput"
        @focus="onFocus"
        @blur="onBlur"
      >
        <slot name="options"></slot>
      </select>
      <vue-google-autocomplete
        v-else-if="type == 'address'"
        id="map"
        classname="form-control"
        :country="['au']"
        ref="vga"
        @inputChange="handleInput"
        @focus="onFocus"
        @blur="onBlur"
        v-on:placechanged="placeChanged"
      />
      <textarea
        v-else-if="type == 'textarea'"
        :value="innerValue"
        class="form-control data-hj-allow"
        data-lpignore="true"
        rows="10"
        @input="handleInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <input
        v-else
        :type="type"
        v-mask="mask"
        :value="innerValue"
        :maxlength="maxlength"
        class="form-control data-hj-allow"
        data-lpignore="true"
        @input="handleInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <slot name="suffix"></slot>
      <span v-if="validated" class="icon check">
        <i class="far fa-check"></i>
      </span>
    </div>
    <div v-if="errors.length && displayErrors" class="form-group--errors">
      {{ errors[0] }}
    </div>
    <div v-else-if="displayErrors" class="form-group--errors">
      {{ errors[0] }}
    </div>
  </div>
</template>
<script>
  import VueGoogleAutocomplete from "vue-google-autocomplete"
  import { createPopper } from "@popperjs/core"
  export default {
    name: "custom-field",
    props: {
      value: {
        default: null,
      },
      label: {
        required: true,
      },
      customLabel: {
        default: "",
      },
      rules: {
        default: null,
      },
      maxlength: {
        defalut: 99999,
      },
      type: {
        default: "text",
      },
      mask: {
        default: null,
      },
      addressInfo: {
        default: null,
      },
      displayErrors: {
        default: true,
      },
    },
    components: {
      VueGoogleAutocomplete,
    },
    data() {
      return {
        optionLabel: "",
        innerValue: "",
        isFocus: false,
        check: false,
        popper: null,
      }
    },
    watch: {
      value() {
        this.updateInnerValue()
      },
    },
    computed: {
      errors() {
        if (!this.check) return []
        const value = this.innerValue
        const errors = []

        if (this.rules) {
          for (const rule in this.rules) {
            const config = this.rules[rule]
            if (rule == "required" && !value) {
              if (config.message) {
                errors.push(config.message)
              } else {
                errors.push("This is a required field")
              }
              break
            }

            if (rule == "maxlength" && value.length > config.value) {
              if (config.message) {
                errors.push(config.message)
              } else {
                errors.push("The max length of the field is " + config.value)
              }
              break
            }

            if (rule == "email") {
              const [email, domain] = value.split("@")

              if (!/^[a-z0-9][a-z0-9-_.+]+$/.test(email)) {
                errors.push(
                  "The domain portion of the email address is invalid (the portion before the @: )"
                )
                break
              }

              if (
                !/^([a-z]|[a-z0-9]?[a-z0-9-]+[a-z0-9]).[a-z0-9]{2,10}(?:.[a-z]{2,10})?$/.test(
                  domain
                )
              ) {
                errors.push(
                  "The domain portion of the email address is invalid (the portion after the @: )"
                )
                break
              }

              if (!email || !domain) {
                errors.push("An email address must contain a single @")
                break
              }

              break
            }

            if (rule == "regexr" && !value.match(new RegExp(config.pattern))) {
              if (config.message) {
                errors.push(config.message)
              } else {
                errors.push("The value doesn't match the pattern")
              }
              break
            }

            if (rule == "address" && !this.addressInfo) {
              if (config.message) {
                errors.push(config.message)
              } else {
                errors.push("Please select a value from the autocomplete.")
              }
              break
            }

            if (rule == "cardNumber" && this.mask.length != value.length) {
              if (config.message) {
                errors.push(config.message)
              } else {
                errors.push("The credit card number is invalid")
              }
              break
            }

            if (rule == "cardCCV" && (value.length < 3 || value.length > 4)) {
              if (config.message) {
                errors.push(config.message)
              } else {
                errors.push("The security code is invalid")
              }
              break
            }

            if (rule == "cardExpiry") {
              let errorMessage = ""

              if (config.message) {
                errorMessage = config.message
              } else {
                errorMessage = "The expiry date is invalid"
              }

              if (value.length != 5) {
                errors.push(errorMessage)
                break
              }

              const [month, year] = value.split("/")
              const today = new Date()

              if (parseInt(month) > 12 || parseInt(month) < 1) {
                errors.push(errorMessage)
                break
              }

              if (today.getFullYear() > parseInt("20" + year)) {
                errors.push(errorMessage)
                break
              }

              if (today.getFullYear() == parseInt("20" + year)) {
                if (today.getMonth() + 1 > parseInt(month)) {
                  errors.push(errorMessage)
                  break
                }
              }
            }
          }
        }

        return errors
      },
      validated() {
        if (!this.check) {
          return false
        }

        if (!this.innerValue) {
          return false
        }

        if (this.errors.length) {
          return false
        }

        return true
      },
      topLabel() {
        return this.isFocus || this.innerValue
      },
    },
    methods: {
      updateInnerValue(forceUpdate) {
        setTimeout(() => {
          this.innerValue = this.value
          this.check = this.check || (!!this.innerValue && forceUpdate);
          if (this.$refs.vga) {
            this.$refs.vga.update(this.innerValue)
          }
        }, 0);
      },
      customBlur(e) {
        const container = document.querySelector(".custom-select--options")
        if (container.contains(e.relatedTarget)) {
          return
        }

        document
          .querySelector(".custom-select--options")
          .classList.add("d-none")
        window.jQuery("body").removeClass("custom-select-open")
        this.onBlur()
      },
      openSelect() {
        this.isFocus = true
        window.jQuery("body").addClass("custom-select-open")
        const tooltip = document.querySelector(".custom-select--options")
        const button = document.querySelector(".custom-select")
        this.popper = createPopper(button, tooltip, {
          placement: "bottom",
          container: "body",
          modifiers: [
            {
              name: "flip",
              options: {
                fallbackPlacements: ["bottom"],
              },
            },
            {
              name: "preventOverflow",
              options: {
                rootBoundary: "document",
              },
            },
            {
              name: "offset",
              options: {
                offset: [0, -40],
              },
            },
          ],
        })
        tooltip.classList.remove("d-none")
      },
      customChange(value) {
        document
          .querySelector(".custom-select--options")
          .classList.add("d-none")
        window.jQuery("body").removeClass("custom-select-open")
        this.innerValue = value
        this.$emit("input", this.innerValue)
        this.onBlur()
      },
      placeChanged(fields) {
        this.$emit("placechanged", fields)
      },
      handleInput(e) {
        if (e.target) {
          this.innerValue = e.target.value
        } else {
          this.innerValue = e.newVal
        }
        this.$emit("input", this.innerValue)
      },
      onFocus() {
        this.isFocus = true
      },
      onBlur() {
        if (!this.check) this.check = true
        this.isFocus = false
        return this.errors
      },
    },
    mounted() {
      this.updateInnerValue(true);
    },
  }
</script>
